import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getApiV5TermsAndConditions,
  getApiV5Profile,
  postApiV5TermsAndConditionsAccept,
  UserProfileRole,
} from '@liscio/api';
import { useBiometrics } from '@liscio/common';

import { useAuth } from './use-auth';
import { config } from 'config';
import { useLaunchDarklyIdentify } from 'custom-hooks/useLaunchDarklyIdentify';
import apiClient from 'fetch-utils/api-client';

const {
  getHomeData,
  getMenuItems,
  changePassword,
  updateProfile,
  updateUserAvatar,
} = apiClient.users;

export function useHomeData() {
  const { isLoggedIn } = useAuth();
  const { identify } = useLaunchDarklyIdentify();
  return useQuery({
    // Only fetch when authenticated
    enabled: isLoggedIn,
    queryKey: ['home-data'],
    queryFn: async () => {
      const homeData = await getHomeData();

      // Update LD Context
      if (homeData) {
        await identify(homeData.data);
      }
      return homeData;
    },
    cacheTime: 36_000_000, // 10 hours
    staleTime: 36_000_000, // 10 hours
  });
}

export function useMenuItemsData() {
  return useQuery({
    queryKey: ['menu-items'],
    queryFn: getMenuItems,
    cacheTime: 36_000_000, // 10 hours
  });
}

export function useProfile(enabled: boolean = true) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: ({ signal }) => getApiV5Profile(signal),
    cacheTime: 36_000_000, // 10 hours
    enabled,
  });
}

export const useProfileData = () => {
  const { data, isLoading: isLoadingProfile } = useProfile();
  const { data: homeData, isLoading: isHomeDataLoading } = useHomeData();

  return {
    cpa_user_id: data?.cpa_user_id,
    avatar: data?.avatar,
    email: data?.email,
    first_name: data?.first_name,
    middle_name: data?.middle_name,
    last_name: data?.last_name,
    name:
      data?.first_name || data?.last_name
        ? `${data.first_name} ${data.last_name}`
        : undefined,
    phone: data?.phone,
    role: data?.role,
    title: data?.title,
    isCustomer: data?.role === UserProfileRole.Client,
    isFirmUser:
      data?.role === UserProfileRole.Firm_Admin ||
      data?.role === UserProfileRole.Firm_Employee,
    isFirmAdmin: data?.role === UserProfileRole.Firm_Admin,
    isLoading: isLoadingProfile || isHomeDataLoading,
    hasSMSElement: homeData?.data?.cpa_sms_enabled || false,
    two_factor_enabled: data?.two_factor_enabled,
    isLiscioAdmin: data?.is_liscio_admin || false,
  };
};

export function useTermsAndConditions() {
  return useQuery({
    queryKey: ['terms-and-conditions'],
    queryFn: ({ signal }) => getApiV5TermsAndConditions(undefined, signal),
    cacheTime: 36_000_000, // 10 hours
  });
}

export function useAcceptTermsAndConditions(
  props?: DefaultMutationHookInterface
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (version: string) =>
      postApiV5TermsAndConditionsAccept({ version }),
    onSuccess: async () => {
      await Promise.all([
        client.refetchQueries(['home-data']),
        client.refetchQueries(['terms-and-conditions']),
      ]);
      props?.onSuccess && props.onSuccess();
    },
  });
}

export function useChangePassword(props?: DefaultMutationHookInterface) {
  const { email } = useProfileData();
  const { saveBiometricCredentials, isBiometricLoginEnabled } = useBiometrics({
    webAppUrl: config.webApp.url,
  });

  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => changePassword({ currentPassword, newPassword }),
    onSuccess: async (_, data) => {
      // Update biometric credentials
      if (email && isBiometricLoginEnabled) {
        await saveBiometricCredentials({ email, password: data.newPassword });
      }

      props?.onSuccess && props.onSuccess();
    },
  });
}

export function useUpdateProfile(props?: DefaultMutationHookInterface) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      firstName,
      middleName,
      lastName,
      phone,
      title,
      isTextingAllowed,
      companyName,
      customLiscioUrl,
    }: {
      firstName: string;
      middleName: string;
      lastName: string;
      phone: string;
      title: string;
      isTextingAllowed: boolean;
      companyName?: string;
      customLiscioUrl?: string;
    }) =>
      updateProfile({
        firstName,
        middleName,
        lastName,
        phone,
        title,
        isTextingAllowed,
        companyName,
        customLiscioUrl,
      }),
    onSuccess: () => {
      client.invalidateQueries(['profile']);
      props?.onSuccess && props.onSuccess();
    },
  });
}

export function useUpdateUserAvatar(props?: DefaultMutationHookInterface) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ base64Picture }: { base64Picture: string }) =>
      updateUserAvatar({ base64Picture }),
    onSuccess: () => {
      client.invalidateQueries(['profile']);
      props?.onSuccess && props.onSuccess();
    },
  });
}
